import * as React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { colors, shadows } from '../../employees/styles/personalakteStyles';

interface WAFilterDnDProps {
  draggableWAList: any[];
  hiddenWorkAreas: number[];
  onHiddenWorkAreasChange: (updater: (prev: number[]) => number[]) => void;
  onDragEnd: (result: any) => void;
  selectedLocations: number[];
  isManager: boolean;
  workAreaIds: number[];
}

function WAFilterDnD({
  draggableWAList,
  hiddenWorkAreas,
  onHiddenWorkAreasChange,
  onDragEnd,
  selectedLocations,
  isManager,
  workAreaIds,
}: WAFilterDnDProps) {
  const hexToRgb = (hex: string) => {
    const cleanHex = hex.replace('#', '');
    return {
      r: parseInt(cleanHex.substring(0, 2), 16),
      g: parseInt(cleanHex.substring(2, 4), 16),
      b: parseInt(cleanHex.substring(4, 6), 16)
    };
  };

  const getWAColor = (waColor: string): string => waColor || '#5271A5';

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="WAFilterDroppable" direction="horizontal">
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              display:'flex',
              alignItems:'center',
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '20px',
              padding: '4px',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: shadows.soft,
            }}
            data-testid="ws-wa-filters"
          >
            {draggableWAList
            .filter((wa: any) => selectedLocations.length === 0 || selectedLocations.includes(wa.location))
            .map((wa, index) => {
              const isVisible = !hiddenWorkAreas.includes(wa.id);
              const isDisabled = isManager && !workAreaIds.includes(wa.id);
              const waColor = getWAColor(wa.color || '#5271A5');
              const rgb = hexToRgb(waColor);

              return (
                <Draggable
                  key={String(wa.id)}
                  draggableId={String(wa.id)}
                  index={index}
                  isDragDisabled={isDisabled}
                >
                  {(provided2) => (
                    <div
                      ref={provided2.innerRef}
                      {...provided2.draggableProps}
                      {...provided2.dragHandleProps}
                      role="button"
                      tabIndex={0}
                      data-testid={`work-area-filter-${wa.id}`}
                      onClick={() => {
                        if (isDisabled) return;
                        if (isVisible) {
                          onHiddenWorkAreasChange(prev => [...prev, wa.id]);
                        } else {
                          onHiddenWorkAreasChange(prev => prev.filter(x => x !== wa.id));
                        }
                      }}
                      onKeyDown={e => {
                        if (isDisabled) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          if (isVisible) {
                            onHiddenWorkAreasChange(prev => [...prev, wa.id]);
                          } else {
                            onHiddenWorkAreasChange(prev => prev.filter(x => x !== wa.id));
                          }
                        }
                      }}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '16px',
                        border: isVisible
                          ? `1px solid rgba(0,0,0,0.2)`
                          : '1px solid transparent',
                        background: isVisible
                          ? waColor
                          : 'rgba(255, 255, 255, 0.9)',
                        color: isVisible ? 'white' : colors.textLight,
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: isDisabled ? 'not-allowed' : 'grab',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginLeft: index > 0 ? '2px' : '0',
                        boxShadow: 'none',
                        textShadow: 'none',
                        opacity: isDisabled ? 0.5 : 1,
                        userSelect: 'none',
                        outline: 'none',
                        ...provided2.draggableProps.style
                      }}
                      onMouseEnter={(e) => {
                        if (isDisabled) return;
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = `0 3px 10px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`;
                      }}
                      onMouseLeave={(e) => {
                        if (isDisabled) return;
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {wa.name}
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default WAFilterDnD;
